import React, { useContext, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';

import { app, media } from 'context';
import Clickable from 'ds/Clickable';
import Layout from 'ds/Layout';
import Text from 'ds/Text';
import { LayoutOverflow, OnMeasure } from 'ds/types';
import { BLUE_500_HEX, Color } from 'shared';

interface Props {
  items: TabItem[];
  top?: number;
  children: JSX.Element[];
  initialSelectedIndex?: number;
  flexGrow?: number;
  menuMarginX?: number;
  backgroundColor?: Color | null; // specify null for transparent background
  textColor?: Color;
  /** Solid bottom border color for the selected tab (defaults to blue). Ignored when `borderGradient` is set on the tab item. */
  indicatorSolidColor?: string;
  /** Selected tab label color (defaults to blue-500). */
  selectedLabelColor?: Color;
  /** Unselected tab label color (defaults to gray-900). */
  inactiveLabelColor?: Color;
  pullLeftXs?: boolean;
  overflow?: LayoutOverflow;
  syncSelectedIndex?: boolean;
  onMeasureTabs?: OnMeasure;
  /** Keep every panel mounted and size to the tallest, so switching tabs does not collapse the layout. */
  keepMounted?: boolean;
}

interface TabItem {
  label: string;
  count?: number;
  pathname?: string;
  search?: string;
  width?: string | number;
  onClick?: VoidFunction;
  tabTextColor?: Color | string;
  borderGradient?: string;
  semibold?: boolean;
}

const Tabs: React.FC<Props> = ({
  items,
  top,
  children,
  initialSelectedIndex = 0,
  flexGrow = 1,
  menuMarginX,
  backgroundColor,
  textColor,
  indicatorSolidColor,
  selectedLabelColor,
  inactiveLabelColor,
  pullLeftXs,
  overflow = 'auto',
  syncSelectedIndex = false,
  onMeasureTabs,
  keepMounted = false
}) => {
  const [selectedIndex, setSelectedIndex] = useState<number>(initialSelectedIndex);
  const history = useHistory();
  const { xs } = useContext(media);
  const { appPaddingX } = useContext(app);

  useEffect(() => {
    if (syncSelectedIndex) {
      setSelectedIndex(initialSelectedIndex);
    }
  }, [initialSelectedIndex, syncSelectedIndex]);

  return (
    <>
      <Layout
        {...(top === undefined ? {} : { position: 'sticky', top })}
        color={backgroundColor === null ? undefined : backgroundColor || 'white'}
        overflow={overflow}
        zIndex={100}
        borderBottom={!xs}
        {...(xs ? {} : { align: 'center' })}
        marginX={menuMarginX}
      >
        <Layout
          align="center"
          overflow="auto"
          flexGrow={flexGrow}
          borderBottom={xs}
          {...(xs
            ? {
                position: 'relative',
                left: pullLeftXs ? -appPaddingX : undefined,
                width: pullLeftXs ? `calc(100% + ${appPaddingX * 2}px)` : undefined
              }
            : {})}
          onMeasure={onMeasureTabs}
        >
          {items.map(
            ({ label, count, pathname, search, width, onClick, tabTextColor, borderGradient, semibold }, index) => {
              const isSelected = index === selectedIndex;

              return (
                <Clickable
                  key={label}
                  onClick={() => {
                    setSelectedIndex(index);

                    onClick && onClick();

                    if (pathname || search) {
                      if (!pathname) {
                        history.push({ search });
                      } else {
                        history.push({ pathname: pathname, search });
                      }
                    }
                  }}
                  style={{
                    flex: '1 1 0',
                    justifyContent: 'center',
                    borderBottom: isSelected
                      ? borderGradient
                        ? '4px solid'
                        : `4px solid ${indicatorSolidColor ?? BLUE_500_HEX}`
                      : '4px solid transparent',
                    borderImageSource: isSelected ? borderGradient : undefined,
                    borderImageSlice: isSelected && borderGradient ? 1 : undefined
                  }}
                >
                  <Layout
                    align="center"
                    paddingTop={12}
                    paddingBottom={12}
                    paddingX={24}
                    position="relative"
                    display="inline-flex"
                    width={width}
                    justify={width ? 'center' : undefined}
                  >
                    {textColor ? (
                      <Text color={textColor} size="body2">
                        {label}
                      </Text>
                    ) : (
                      <>
                        <Layout position="absolute" left={24} top={11}>
                          <Text
                            size="body2"
                            {...(isSelected
                              ? {
                                  color: selectedLabelColor ?? 'blue-500',
                                  semibold: true
                                }
                              : { color: inactiveLabelColor ?? 'gray-900', semibold: semibold ? semibold : false })}
                            wrap={false}
                            __style={
                              tabTextColor?.includes('gradient')
                                ? {
                                    background: tabTextColor,
                                    WebkitBackgroundClip: 'text',
                                    WebkitTextFillColor: 'transparent',
                                    backgroundClip: 'text',
                                    color: 'transparent'
                                  }
                                : undefined
                            }
                          >
                            {label}
                          </Text>
                        </Layout>
                        <Layout __style={{ visibility: 'hidden' }}>
                          <Text size="body2" semibold wrap={false}>
                            {label}
                          </Text>
                        </Layout>
                      </>
                    )}
                    {typeof count === 'number' && (
                      <Layout marginLeft={8}>
                        <Layout
                          color={isSelected ? 'blue-25' : 'gray-25'}
                          paddingX={4}
                          paddingY={4}
                          borderRadius={4}
                          minWidth={24}
                          justify="center"
                        >
                          <Text size="tag" color={isSelected ? 'blue-500' : 'gray-900'}>
                            {count}
                          </Text>
                        </Layout>
                      </Layout>
                    )}
                  </Layout>
                </Clickable>
              );
            }
          )}
        </Layout>
      </Layout>
      {keepMounted ? (
        <Layout display="grid" width="100%">
          {children.map((child, index) => {
            const isSelected = index === selectedIndex;

            return (
              <Layout
                key={items[index]?.label ?? index}
                width="100%"
                __style={{
                  gridArea: '1 / 1',
                  visibility: isSelected ? 'visible' : 'hidden',
                  zIndex: isSelected ? 1 : 0,
                  pointerEvents: isSelected ? 'auto' : 'none'
                }}
              >
                {child}
              </Layout>
            );
          })}
        </Layout>
      ) : (
        children[selectedIndex]
      )}
    </>
  );
};

export default Tabs;
